import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance} from 'vue';
import { formatWithLocale } from '@white-label-helper/date-utilities';
import type { Day } from '../cavu-calendar.types.d.ts';


export default /*#__PURE__*/_defineComponent({
  __name: 'cavu-calendar-days',
  props: {
    days: { default: () => [] },
    mode: { default: 'single' },
    includeTrailingCells: { type: Boolean, default: true },
    startWeekOn: { default: 'monday' },
    showWeekdayHeader: { type: Boolean, default: true }
  },
  emits: ["select-day", "esc-pressed", "pageup-pressed", "pagedown-pressed", "arrow-pressed"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'select-day', day: Day): void
  (e: 'esc-pressed'): void
  (e: 'pageup-pressed'): void
  (e: 'pagedown-pressed'): void
  (e: 'arrow-pressed', event: KeyboardEvent, day: Day): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { days: Day[], mode: 'single' | 'multiple' | 'range', includeTrailingCells: boolean, startWeekOn: 'sunday' | 'monday', showWeekdayHeader: boolean };





// Instance proxy for translations
const proxy = getCurrentInstance()?.proxy as Vue;

/**
 * Calculate the weekday index of the first day of the month (0 = Sunday, 6 = Saturday)
 */
const firstDayOfWeek = computed(() => {
  if (!props.days.length) return 0;
  const currentMonth = new Date(props.days[0].date)
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  return props.startWeekOn === 'sunday'
    ? new Date(year, month, 1).getDay()
    : (new Date(year, month, 1).getDay() + 6) % 7;
});

/**
 * Generate localized weekday names based on the current locale and week start preference.
 */
const daysOfTheWeek = computed(() => {
  const locale = proxy.$i18n.locale;

  const baseDate = new Date(Date.UTC(2026, 0, 4)); // Start with a Sunday
  const localizedDays = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(baseDate);
    day.setUTCDate(baseDate.getUTCDate() + i);
    return {
      abbr: formatWithLocale(day, 'eeeeee', locale),
      full: formatWithLocale(day, 'eeee', locale),
    };
  });

  if (props.startWeekOn === 'sunday') {
    return localizedDays;
  } else {
    return [...localizedDays.slice(1), localizedDays[0]]; // Move Sunday to the end
  }
});

function selectDate(day: Day) {
  if (day.disabled) return;
  emit('select-day', day)
}

/**
 * Handle tab key navigation from a day cell. If the next month button is disabled, focus the previous month button, and vice versa.
 */
function tabFromDay() {
  const nextButton = document.querySelector('button[data-testid="calendar--next-month"]') as HTMLButtonElement | null;
  const prevButton = document.querySelector('button[data-testid="calendar--prev-month"]') as HTMLButtonElement | null;
  prevButton?.disabled
    ? nextButton?.focus()
    : prevButton?.focus();
}

function getFirstSelectableDay() {
  return props.days.find(day => !day.disabled) || null;
}

function getLastSelectableDay() {
  return [...props.days].reverse().find(day => !day.disabled) || null;
}

function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

function getDayAriaLabel(day: Day): string {
  const base = formatWithLocale(day.date, 'EEEE d MMMM yyyy', proxy.$i18n.locale);
  if (props.mode === 'range') {
    if (day.startRange) return `${base}, start of selected range`;
    if (day.endRange) return `${base}, end of selected range`;
    if (day.inRange) return `${base}, within selected range`;
  }
  return base;
}

return { __sfc: true,props, emit, proxy, firstDayOfWeek, daysOfTheWeek, selectDate, tabFromDay, getFirstSelectableDay, getLastSelectableDay, isToday, getDayAriaLabel }
}

})